import { ExportProgressReporter } from "../../components/CustomButton";
import React from "react";
import { Box, Card, CardContent, CircularProgress, Divider } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import CustomLabel from "../../components/CustomLabel";
import PageHeader from "../../components/PageHeader";
import { useParams } from "react-router-dom";
import { getAllBranches } from "../../services/BranchesService";
import { getCashflowReport } from "../../services/newReportsService";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatTableDate } from "../../utils/tableDateFormat";

const REPORTS_PAGE_SIZE = 30;

const Reports: React.FC = () => {
  const [search, setSearch] = React.useState<string>("");
  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  });
  const params = useParams<{ businessId?: string; id?: string }>();
  const venueId = params.businessId || params.id;

  const [units, setUnits] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<string | number>("all");
  const [loadingUnits, setLoadingUnits] = React.useState(false);
  const [reportsData, setReportsData] = React.useState<any[]>([]);
  const [summary, setSummary] = React.useState<any | null>(null);
  const [loadingReports, setLoadingReports] = React.useState(false);
  const [businessSummary, setBusinessSummary] = React.useState<any | null>(null);
  const [loadingBusinessSummary, setLoadingBusinessSummary] = React.useState(false);
  const [totalElements, setTotalElements] = React.useState<number>(0);
  const [totalPages, setTotalPages] = React.useState<number>(0);

  const normalizeRange = React.useCallback(
    (range: { start?: string; end?: string } | [string?, string?] | null) => {
      const buildBoundary = (raw: string | Date | undefined | null, endOfDay: boolean) => {
        if (!raw) return null;

        // Parse plain date strings as UTC day boundaries to avoid timezone shifts
        if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          const [y, m, d] = raw.split("-").map(Number);
          const ts = Date.UTC(
            y,
            m - 1,
            d,
            endOfDay ? 23 : 0,
            endOfDay ? 59 : 0,
            endOfDay ? 59 : 0,
            endOfDay ? 999 : 0
          );
          return new Date(ts);
        }

        const parsed = raw instanceof Date ? raw : new Date(raw);
        if (Number.isNaN(parsed.getTime())) return null;

        return new Date(
          Date.UTC(
            parsed.getUTCFullYear(),
            parsed.getUTCMonth(),
            parsed.getUTCDate(),
            endOfDay ? 23 : 0,
            endOfDay ? 59 : 0,
            endOfDay ? 59 : 0,
            endOfDay ? 999 : 0
          )
        );
      };

      if (!range) return { startDate: null as Date | null, endDate: null as Date | null };
      const startRaw = Array.isArray(range) ? range[0] : range.start;
      const endRaw = Array.isArray(range) ? range[1] : range.end;

      const startDate = buildBoundary(startRaw, false);
      const endDate = buildBoundary(endRaw, true);

      return { startDate, endDate };
    },
    []
  );

  const isWithinRange = React.useCallback(
    (dateValue: any) => {
      const { startDate, endDate } = normalizeRange(dateRange);
      if (!startDate && !endDate) return true;
      const d = dateValue ? new Date(dateValue) : null;
      if (!d || Number.isNaN(d.getTime())) return false;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    },
    [dateRange, normalizeRange]
  );

  const getUnitId = React.useCallback((unit: any): string | number | null => {
    const raw = unit?.id ?? unit?.unit_id ?? unit?._id ?? unit?.uuid;
    return raw === undefined || raw === null || raw === "" ? null : raw;
  }, []);

  const displayUnits = React.useMemo(
    () => units.filter((u: any) => u && u.name && getUnitId(u) !== null),
    [getUnitId, units]
  );

  React.useEffect(() => {
    const fetchBranches = async () => {
      console.log("fetchUnits called with venueId:", venueId);
      setLoadingUnits(true);
      try {
        if (!venueId) {
          console.error("No venue ID available, venueId:", venueId);
          setUnits([]);
          return;
        }

        const response = await getAllBranches(venueId);
        let unitsData = [];
        if (response && response.units) {
          unitsData = response.units;
        } else if (response && response.data) {
          unitsData = response.data;
        } else if (Array.isArray(response)) {
          unitsData = response;
        }

        console.log("Processed units data:", unitsData);
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      } catch (error) {
        console.error("Error fetching units:", error);
        setUnits([]);
      } finally {
        setLoadingUnits(false);
      }
    };

    if (venueId) {
      fetchBranches();
    } else {
      console.log("No venueId available, skipping unit fetch");
    }
  }, [venueId]);

  type CashflowResponse = {
    income?: {
      rows?: any[];
    };
    expenses?: {
      rows?: any[];
    };
    summary?: any;
  };

  // One page of income and one of expenses, filtered and searched by the
  // server; the summary it returns covers the whole period.
  const loadCashflowPage = React.useCallback(
    async ({
      page,
      size,
      search: searchTerm,
    }: {
      page: number;
      size: number;
      search?: string;
    }): Promise<{
      rows: any[];
      totalElements: number;
      totalPages: number;
      summary: any | null;
    }> => {
      if (!venueId || !dateRange) {
        return { rows: [], totalElements: 0, totalPages: 1, summary: null };
      }

      const selectedUnitId =
        selectValue !== "all" && selectValue !== "" ? String(selectValue) : null;

      const { startDate, endDate } = normalizeRange(dateRange);
      const from = startDate ? startDate.toISOString() : "";
      const to = endDate ? endDate.toISOString() : "";

      {
        const res = (await getCashflowReport({
          from,
          to,
          businessId: venueId,
          unitId: selectedUnitId || undefined,
          page,
          size,
          search: searchTerm,
        })) as CashflowResponse;
        const reportPages: CashflowResponse[] = [res];
        const incomeTotal = Number((res as any)?.income?.pagination?.totalElements) || 0;
        const expenseTotal = Number((res as any)?.expenses?.pagination?.totalElements) || 0;
        const apiTotalElements = incomeTotal + expenseTotal;
        const apiTotalPages = Math.max(1, Number((res as any)?.totalPages) || 1);

        const apiContentPages = reportPages
          .map((page: any) =>
            Array.isArray(page?.content)
              ? (page.content as any[])
              : Array.isArray(page?.data?.content)
              ? (page.data.content as any[])
              : null
          )
          .filter((content): content is any[] => Array.isArray(content));
        const apiContent =
          apiContentPages.length > 0 ? apiContentPages.flat() : null;

        const incomeRows =
          reportPages.flatMap((page) => page?.income?.rows || []).map((r: any) => {
            const invoiceLabel = r.invoiceId ? `Invoice #${r.invoiceId}` : "Invoice";
            const clientLabel = r.clientName || "-";
            return {
              id: `income-${r.invoiceId}`,
              // Keep table title unchanged (invoice + client) while CSV gets split columns
              title: `${invoiceLabel} - ${clientLabel}`,
              invoiceNumber: r.invoiceId ? `Invoice #${r.invoiceId}` : "-",
              clientName: r.clientName || "-",
              clientPhone: r.clientPhone || r.phone || r.client?.phone || "-",
              service: r.serviceName || "-",
              invoiceStatus:
                r.invoiceStatus ||
                r.invoice_status ||
                r.invoice?.status ||
                r.invoice?.status_name ||
                r.status ||
                "-",
              bookingNote:
                r.bookingNote ||
                r.booking_note ||
                r.notes ||
                r.invoiceNotes ||
                r.invoice_notes ||
                r.appointment?.notes ||
                "-",
              promoCode:
                r.promoCode ||
                r.promo_code ||
                r.invoice?.promoCode ||
                r.invoice?.promo_code ||
                "-",
              amount: r.moneyReceived,
              totalAmount: r.totalAmount,
              paidAmount: r.moneyReceived,
              remainingAmount: r.remainingAmount,
              // Prefer appointment start date, then invoice date, then created date
              date: r.appointmentStartAt || r.invoiceDate || r.createdAt,
              businessId: r.business?.id,
              unitId: r.unit?.id || null,
              unitName: r.unit?.name || "-",
              description: r.serviceName,
              type: "income",
            };
          });

        const expenseRows =
          reportPages.flatMap((page) => page?.expenses?.rows || []).map((e: any, idx: number) => {
            const expenseUnitId = e?.metadata?.unitId ?? selectedUnitId ?? null;
            const matchedUnit = units.find(
              (u: any) =>
                String(u?.id ?? u?.unit_id ?? u?._id ?? "") ===
                String(expenseUnitId ?? "")
            );
            const isPurchase =
              String(e?.metadata?.type || "").toUpperCase() === "PURCHASE";
            return {
              id: `expense-${e?.expenseId || e?.id || idx}`,
              title: e?.expenseTitle || e?.expenseName || e?.title || "Unknown Expense",
              invoiceNumber: "-",
              clientName: "-",
              clientPhone: "-",
              service: isPurchase ? "Purchase" : "Expense",
              invoiceStatus: e?.paid ? "PAID" : "-",
              bookingNote: "-",
              promoCode: "-",
              amount: -Math.abs(e?.amount || 0),
              totalAmount: Math.abs(e?.amount || 0),
              paidAmount: Math.abs(e?.amount || 0),
              remainingAmount: 0,
              date:
                e?.expenseDate ||
                e?.paidAt ||
                e?.createdAt ||
                e?.paid_at ||
                e?.created_at,
              businessId: e?.metadata?.businessId || e?.business?.id || null,
              unitId: expenseUnitId || null,
              unitName: matchedUnit?.name || e?.unit?.name || e?.business?.name || e?.metadata?.unitName || "-",
              description: e?.expenseName || e?.description,
              type: "expense",
            };
          }) || [];

        const combinedRows = [...incomeRows, ...expenseRows].sort((a, b) => {
          // Put zero amounts at the bottom
          const aIsZero = a.amount === 0;
          const bIsZero = b.amount === 0;
          if (aIsZero && !bIsZero) return 1;
          if (!aIsZero && bIsZero) return -1;
          // For non-zero amounts, sort by date (newest first)
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });

        const normalizedRows = (apiContent ?? combinedRows).map((row: any) => {
          const isExpense =
            String(row?.type || "").toLowerCase() === "expense" || Number(row?.amount) < 0;
          if (!isExpense) return row;

          return {
            ...row,
            // Prefer explicit expense title/name over generic description in table display.
            title: row?.expenseTitle || row?.expenseName || row?.title || "Unknown Expense",
          };
        });

        const rowsToDisplay = normalizedRows.slice().sort((a: any, b: any) => {
          const aAmount = Number(a?.amount ?? 0);
          const bAmount = Number(b?.amount ?? 0);
          const aIsZero = aAmount === 0;
          const bIsZero = bAmount === 0;
          if (aIsZero && !bIsZero) return 1;
          if (!aIsZero && bIsZero) return -1;

          const aDate = new Date(a?.date ?? a?.invoiceDate ?? a?.createdAt ?? 0);
          const bDate = new Date(b?.date ?? b?.invoiceDate ?? b?.createdAt ?? 0);
          return bDate.getTime() - aDate.getTime();
        });

        return {
          rows: rowsToDisplay,
          totalElements: apiTotalElements,
          totalPages: apiTotalPages,
          summary: res?.summary ?? null,
        };
      }
    },
    [venueId, dateRange, selectValue, units, normalizeRange]
  );

  const debouncedSearch = useDebouncedValue(search, 400);

  const fetchReportsTablePage = React.useCallback(
    async (params?: any) => {
      const page = Number(params?.page) || 0;
      const size = Number(params?.size) || REPORTS_PAGE_SIZE;
      setLoadingReports(true);
      try {
        const result = await loadCashflowPage({
          page,
          size,
          search: String(params?.search || ""),
        });
        setReportsData(result.rows);
        setSummary(result.summary);
        return {
          content: result.rows,
          number: page,
          size,
          totalElements: result.totalElements,
          totalPages: result.totalPages,
        };
      } catch (err) {
        console.error("Failed to fetch cashflow:", err);
        setReportsData([]);
        setSummary(null);
        throw err;
      } finally {
        setLoadingReports(false);
      }
    },
    [loadCashflowPage]
  );


  // Fetch overall business summary (no unit filter)
  React.useEffect(() => {
    let cancelled = false;

    const fetchBusinessSummary = async () => {
      if (!venueId || !dateRange) {
        setBusinessSummary(null);
        return;
      }

      const { startDate, endDate } = normalizeRange(dateRange);
      const from = startDate ? startDate.toISOString() : "";
      const to = endDate ? endDate.toISOString() : "";

      setLoadingBusinessSummary(true);
      try {
        const res = (await getCashflowReport({
          from,
          to,
          businessId: venueId,
          page: 0,
          size: 1,
        })) as CashflowResponse;

        if (cancelled) return;
        setBusinessSummary(res?.summary ?? null);
      } catch (err) {
        console.error("Failed to fetch business summary:", err);
        if (!cancelled) setBusinessSummary(null);
      } finally {
        if (!cancelled) setLoadingBusinessSummary(false);
      }
    };

    fetchBusinessSummary();

    return () => {
      cancelled = true;
    };
  }, [dateRange, venueId]);

  const exportXlsx = React.useCallback(async (reportProgress?: ExportProgressReporter) => {
    // The table holds one page; the file gets the whole period.
    const reportsData: any[] = [];
    try {
      for (let page = 0; page < 1000; page += 1) {
        const result = await loadCashflowPage({ page, size: 200 });
        reportsData.push(...result.rows);
        reportProgress?.(page + 1, result.totalPages);
        if (page + 1 >= result.totalPages || result.rows.length === 0) break;
      }
    } catch (err) {
      console.error("Failed to export cashflow:", err);
      return;
    }
    if (reportsData.length === 0) return;
    const toExportNumber = (value: any) => {
      if (value === null || value === undefined || value === "") return "-";
      const normalized =
        typeof value === "string" ? value.replace(/[$,\s]/g, "") : value;
      const numeric = Number(normalized);
      return Number.isFinite(numeric) ? numeric : String(value ?? "");
    };

    const columns = [
      { key: "date", header: "Date" },
      { key: "title", header: "invoice Details" },
      { key: "clientName", header: "Client Name" },
      { key: "clientPhone", header: "Mobile Nub" },
      { key: "service", header: "Service" },
      { key: "invoiceStatus", header: "Status" },
      { key: "bookingNote", header: "Booking Note" },
      { key: "promoCode", header: "Promo Code" },
      { key: "unitName", header: "Court Name" },
      { key: "totalAmount", header: "TOTAL Amount" },
      { key: "paidAmount", header: "PAID Amount" },
      { key: "remainingAmount", header: "Remaining" },
    ];

    const extractClientName = (row: any): string => {
      if (row?.clientName) return String(row.clientName);
      const title = String(row?.title || "");
      const split = title.split(" - ");
      return split.length > 1 ? String(split.slice(1).join(" - ")) : "-";
    };

    const reportRows = reportsData.map((r: any) => {
      const isInvoice = String(r?.type || "").toLowerCase() === "income";
      const clientName = isInvoice ? extractClientName(r) : "-";
      const clientPhone = isInvoice ? String(r?.clientPhone || "-") : "-";
      const service = isInvoice
        ? String(r?.service || r?.description || "-")
        : String(r?.service || "-");
      const row: Record<string, any> = {
        // Keep title as-is so invoice number remains in title for invoice rows.
        title: r.title ?? "-",
        clientName,
        clientPhone,
        service,
        invoiceStatus:
          r.invoiceStatus ||
          r.invoice_status ||
          r.invoice?.status ||
          r.invoice?.status_name ||
          r.status ||
          "-",
        bookingNote:
          r.bookingNote ||
          r.booking_note ||
          r.notes ||
          r.invoiceNotes ||
          r.invoice_notes ||
          r.appointment?.notes ||
          "-",
        promoCode:
          r.promoCode ||
          r.promo_code ||
          r.invoice?.promoCode ||
          r.invoice?.promo_code ||
          "-",
        unitName: r.unitName ?? "-",
        totalAmount: toExportNumber(r.totalAmount),
        paidAmount: toExportNumber(r.paidAmount),
        remainingAmount: toExportNumber(r.remainingAmount),
        date: (() => {
          const d = new Date(r.date);
          return Number.isNaN(d.getTime()) ? String(r.date ?? "-") : formatTableDate(d);
        })(),
      };

      return columns.map((column) => row[column.key] ?? "-");
    });

    const sheetRows: any[][] = [
      columns.map((column) => column.header),
      ...reportRows,
    ];
    const cardData = [
      { label: "Invoiced", value: businessSummary?.totalInvoiced },
      { label: "Received", value: businessSummary?.totalReceived },
      { label: "Outstanding", value: businessSummary?.totalOutstanding },
      { label: "Expenses", value: businessSummary?.totalExpensesPaid },
      { label: "Net Cash", value: businessSummary?.netCash },
    ].filter((c) => c.value !== undefined && c.value !== null);

    if (cardData.length > 0) {
      sheetRows.push([]);
      sheetRows.push(["Summary"]);
      sheetRows.push(["Label", "Value"]);
      sheetRows.push(...cardData.map((c) => [c.label, toExportNumber(c.value)]));
    }

    const escapeXml = (value: any) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    const columnName = (index: number) => {
      let name = "";
      let current = index + 1;
      while (current > 0) {
        const remainder = (current - 1) % 26;
        name = String.fromCharCode(65 + remainder) + name;
        current = Math.floor((current - 1) / 26);
      }
      return name;
    };
    const worksheetRows = sheetRows
      .map((row, rowIndex) => {
        const cells = row
          .map((cell, colIndex) => {
            const style = rowIndex === 0 ? ' s="1"' : "";
            if (typeof cell === "number" && Number.isFinite(cell)) {
              return `<c r="${columnName(colIndex)}${rowIndex + 1}"${style}><v>${cell}</v></c>`;
            }
            return `<c r="${columnName(colIndex)}${rowIndex + 1}" t="inlineStr"${style}><is><t>${escapeXml(
              cell
            )}</t></is></c>`;
          })
          .join("");
        return `<row r="${rowIndex + 1}">${cells}</row>`;
      })
      .join("");
    const worksheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols>${columns
      .map((_, idx) => `<col min="${idx + 1}" max="${idx + 1}" width="18" customWidth="1"/>`)
      .join("")}</cols><sheetData>${worksheetRows}</sheetData></worksheet>`;
    const workbookXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Reports" sheetId="1" r:id="rId1"/></sheets></workbook>';
    const workbookRelsXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>';
    const relsXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>';
    const stylesXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="1"><fill><patternFill patternType="none"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs></styleSheet>';
    const contentTypesXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>';

    const encoder = new TextEncoder();
    const crcTable = Array.from({ length: 256 }, (_, n) => {
      let c = n;
      for (let k = 0; k < 8; k += 1) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      }
      return c >>> 0;
    });
    const crc32 = (data: Uint8Array) => {
      let crc = 0xffffffff;
      data.forEach((byte) => {
        crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
      });
      return (crc ^ 0xffffffff) >>> 0;
    };
    const u16 = (value: number) => [value & 0xff, (value >>> 8) & 0xff];
    const u32 = (value: number) => [
      value & 0xff,
      (value >>> 8) & 0xff,
      (value >>> 16) & 0xff,
      (value >>> 24) & 0xff,
    ];
    const files = [
      { name: "[Content_Types].xml", content: contentTypesXml },
      { name: "_rels/.rels", content: relsXml },
      { name: "xl/workbook.xml", content: workbookXml },
      { name: "xl/_rels/workbook.xml.rels", content: workbookRelsXml },
      { name: "xl/styles.xml", content: stylesXml },
      { name: "xl/worksheets/sheet1.xml", content: worksheetXml },
    ];
    const localParts: Uint8Array[] = [];
    const centralParts: Uint8Array[] = [];
    let offset = 0;

    files.forEach((file) => {
      const nameBytes = encoder.encode(file.name);
      const data = encoder.encode(file.content);
      const crc = crc32(data);
      const localHeader = new Uint8Array([
        ...u32(0x04034b50),
        ...u16(20),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u32(crc),
        ...u32(data.length),
        ...u32(data.length),
        ...u16(nameBytes.length),
        ...u16(0),
      ]);
      localParts.push(localHeader, nameBytes, data);

      const centralHeader = new Uint8Array([
        ...u32(0x02014b50),
        ...u16(20),
        ...u16(20),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u32(crc),
        ...u32(data.length),
        ...u32(data.length),
        ...u16(nameBytes.length),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u32(0),
        ...u32(offset),
      ]);
      centralParts.push(centralHeader, nameBytes);
      offset += localHeader.length + nameBytes.length + data.length;
    });

    const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
    const endRecord = new Uint8Array([
      ...u32(0x06054b50),
      ...u16(0),
      ...u16(0),
      ...u16(files.length),
      ...u16(files.length),
      ...u32(centralSize),
      ...u32(offset),
      ...u16(0),
    ]);
    const blob = new Blob([...localParts, ...centralParts, endRecord], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reports-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [loadCashflowPage, businessSummary]);

  const summaryCurrency = React.useMemo(() => {
    const currencies = Object.keys(summary?.totalsByCurrency || {});
    return currencies[0] || "USD";
  }, [summary]);

  const formatMoney = (value: any, currency: string = summaryCurrency) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value ?? "");
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
      }).format(num);
    } catch {
      return `${num}`;
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Reports"
          subtitle="Financial overview by branch."
          sx={{ mt: 2 }}
        />

        <Box sx={{ mb: 3 }}>
          {loadingUnits || loadingBusinessSummary ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 160,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: { xs: 2, sm: 3 },
                gridTemplateColumns: {
                  xs: "repeat(1, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                  lg: "repeat(5, minmax(0, 1fr))",
                },
              }}
            >
              {[
                {
                  label: "Invoiced",
                  value: businessSummary?.totalInvoiced,
                  icon: <ReceiptLongIcon />,
                  color: COLORS.MainColor,
                },
                {
                  label: "Received",
                  value: businessSummary?.totalReceived,
                  icon: <PaymentsIcon />,
                  color: COLORS.MainColor,
                },
                {
                  label: "Outstanding",
                  value: businessSummary?.totalOutstanding,
                  icon: <PendingActionsIcon />,
                  color: COLORS.MainColor,
                },
                {
                  label: "Expenses",
                  value: businessSummary?.totalExpensesPaid,
                  icon: <MoneyOffIcon />,
                  color: COLORS.MainColor,
                },
                {
                  label: "Net Cash",
                  value: businessSummary?.netCash,
                  icon: <AccountBalanceWalletIcon />,
                  color: COLORS.MainColor,
                },
              ].map((card, idx) => (
                <Card
                  key={card.label}
                  sx={{
                    borderRadius: 4,
                    boxShadow: 2,
                    minHeight: 150,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                    bgcolor: COLORS.WhiteBackground,
                    border: "1px solid " + COLORS.BorderColor,
                  }}
                >
                  <CardContent sx={{ position: "relative", zIndex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", color: card.color, fontSize: 32 }}>
                        {card.icon}
                      </Box>
                      <CustomLabel
                        text={card.label}
                        fontWeight={700}
                        sx={{ color: COLORS.BlackText, fontSize: "0.95rem" }}
                      />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <CustomLabel
                        text={formatMoney(card.value, summaryCurrency)}
                        fontWeight={800}
                        sx={{
                          color: card.color,
                          fontSize: { xs: "1.25rem", sm: "1.3rem" },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        <Divider
          sx={{
            my: { xs: 2, sm: 3 },
            borderColor: COLORS.BorderColor,
            borderBottomWidth: 2,
          }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => setDateRange(v),
            fullWidth: false,
          }}
          selectConfig={{
            options: [
              { label: "All", value: "all" },
              ...displayUnits.map((unit) => ({
                label: unit.name || "Unknown Unit",
                value: getUnitId(unit) as any,
              })),
            ],
            value: selectValue,
            onChange: (v: any) => {
              const next = v === "" || v === null || v === undefined ? "all" : v;
              setSelectValue(next);
            },
            placeholder: "Select unit",
            size: "medium",
              label: "Select Unit",

            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              label: "Export XLSX",
              onClick: (reportProgress) => exportXlsx(reportProgress),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{
            page: 0,
            size: REPORTS_PAGE_SIZE,
            search: debouncedSearch,
            unit: String(selectValue ?? ""),
            dateRange: JSON.stringify(dateRange ?? null),
          }}
          fields={[
            { key: "title", header: "Title" },
            { key: "unitName", header: "Unit/Branch" },
            {
              key: "serviceName",
              header: "Service Name",
              transform: (r: any) => r.serviceName || r.service || r.description || "-",
            },
            {
              key: "invoiceStatus",
              header: "Status",
              transform: (r: any) =>
                r.invoiceStatus ||
                r.invoice_status ||
                r.invoice?.status ||
                r.invoice?.status_name ||
                r.status ||
                "-",
            },
            {
              key: "totalAmount",
              header: "Total Amount",
              transform: (r: any) => {
                if (r.totalAmount === null || r.totalAmount === undefined) return "-";
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(r.totalAmount);
              },
            },
            {
              key: "paidAmount",
              header: "Paid",
              transform: (r: any) => {
                if (r.paidAmount === null || r.paidAmount === undefined) return "-";
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(r.paidAmount);
              },
            },
            {
              key: "remainingAmount",
              header: "Remaining",
              transform: (r: any) => {
                if (r.remainingAmount === null || r.remainingAmount === undefined) return "-";
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(r.remainingAmount);
              },
            },
            {
              key: "amount",
              header: "Net Amount",
              transform: (r: any) => {
                if (typeof r.amount !== "number") return r.amount;
                const formatted = new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(Math.abs(r.amount));
                if (r.amount > 0) {
                  return <span style={{ color: "green" }}>+{formatted}</span>;
                } else if (r.amount < 0) {
                  return <span style={{ color: "red" }}>-{formatted}</span>;
                }
                return formatted;
              },
            },
            {
              key: "date",
              header: "Date",
              transform: (r: any) => {
                const d = new Date(r.date);
                return Number.isNaN(d.getTime())
                  ? String(r.date || "")
                  : formatTableDate(d);
              },
            },
          ]}
          fetchFunc={fetchReportsTablePage}
        />
        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: "0 0 10px 10px",
            padding: "16px",
            marginTop: "-10px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CustomLabel
            text="Total Net Amount:"
            sx={{ color: COLORS.typographyColor }}
          />
          <CustomLabel
            text={new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(
              (Number(summary?.netCash) ||
                reportsData
                .filter((r: any) => {
                  if (!isWithinRange(r.date)) return false;

                  if (selectValue && selectValue !== "all") {
                    const itemUnitId =
                      r.unit_id ?? r.unitId ?? null;
                    if (String(itemUnitId) !== String(selectValue)) {
                      return false;
                    }
                  }
                  return true;
                })
                .filter((r: any) => {
                  if (!search) return true;
                  const normalizedQuery = String(search || "")
                    .toLowerCase()
                    .trim();
                  const haystack = [
                    r.title,
                    r.description,
                    r.amount,
                    r.date,
                    r.createdBy,
                  ]
                    .filter(Boolean)
                    .map((v: any) => String(v).toLowerCase())
                    .join(" ");
                  return haystack.includes(normalizedQuery);
                })
                .reduce((sum: number, r: any) => {
                  return sum + (typeof r.amount === "number" ? r.amount : 0);
                }, 0))
            )}
            fontWeight="bold"
            sx={{ color: COLORS.MainColor }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Reports;
